// QR scanning in the mobile apps (tauri-plugin-barcode-scanner, apps/tauri). The camera
// runs behind the WebView ("windowed"), so while scanning the page turns transparent
// (html.scanning, style.css) and draws its own frame and Cancel (ScanButton.vue).
import { appInvoke, isMobileApp } from '../platform'

type PermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'

/** Only the phone apps have a camera scanner; the desktop app and the browser don't. */
export const canScan = isMobileApp

export class ScanError extends Error {}

async function cameraAllowed(): Promise<boolean> {
  const { camera } = await appInvoke<{ camera: PermissionState }>('plugin:barcode-scanner|check_permissions')
  if (camera === 'granted') return true
  // Asking again after "Don't allow" answers denied at once, without a dialog.
  const asked = await appInvoke<{ camera: PermissionState }>('plugin:barcode-scanner|request_permissions')
  return asked.camera === 'granted'
}

/** Scans one QR code: its text, or null when cancelled. */
export async function scanQr(): Promise<string | null> {
  if (!(await cameraAllowed())) {
    throw new ScanError('hrth can’t use the camera. Allow it in Settings › Apps › hrth › Permissions.')
  }
  document.documentElement.classList.add('scanning')
  try {
    const { content } = await appInvoke<{ content: string }>('plugin:barcode-scanner|scan', {
      windowed: true,
      formats: ['QR_CODE'],
    })
    return content
  } catch (e) {
    if (String(e).includes('cancel')) return null
    throw new ScanError(`Couldn’t scan: ${e instanceof Error ? e.message : String(e)}`)
  } finally {
    document.documentElement.classList.remove('scanning')
  }
}

/** Stops a scan in progress (scanQr then returns null). */
export function cancelScan(): void {
  void appInvoke('plugin:barcode-scanner|cancel').catch(() => undefined)
}
